// @ts-ignore
/* eslint-disable */
import { request } from '@umijs/max';
import type { RequestOptions } from '@@/plugin-request/request';
import { getAuthToken } from '@/utils/authPersistence';

/**
 * Warehouse API Service
 */

export interface WarehouseAddress {
  fullName: string;
  mobile: string;
  phone?: string;
  province: string;
  city: string;
  district: string;
  town?: string;
  addressLine: string;
  postalCode: string;
  districtCode: string;
  addressCode: string;
}

export interface Warehouse {
  _id: string;
  name: string;
  code: string;
  address: WarehouseAddress;
  isDefault: boolean;
  isActive: boolean;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  __v?: number;
}

export interface WarehouseResponse {
  status: string;
  statusCode: number;
  message?: string;
  data: {
    warehouse?: Warehouse;
    warehouses?: Warehouse[];
    total?: number;
  };
  timestamp: string;
}

export interface CreateWarehouseParams {
  name: string;
  code: string;
  address: WarehouseAddress;
  notes?: string;
}

export interface UpdateWarehouseParams {
  name?: string;
  code?: string;
  address?: Partial<WarehouseAddress>;
  isDefault?: boolean;
  isActive?: boolean;
  notes?: string;
}

/**
 * Create warehouse
 * POST /admin/warehouses
 */
export async function createWarehouse(
  body: CreateWarehouseParams,
  options?: RequestOptions,
): Promise<WarehouseResponse> {
  try {
    // Ensure Authorization header is included
    const { useAuthStore } = require('@/stores/authStore');
    const token = useAuthStore.getState().token || getAuthToken();

    const requestOptions: any = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      data: body,
      ...(options || {}),
    };

    const response = await request<WarehouseResponse>('/admin/warehouses', requestOptions);
    return response;
  } catch (error: any) {
    // console.error('❌ Create Warehouse - Error:', {
    //   status: error?.response?.status,
    //   statusText: error?.response?.statusText,
    //   data: error?.response?.data,
    //   message: error?.message,
    //   requestBody: body,
    // });
    throw error;
  }
}

/**
 * Get all warehouses
 * GET /admin/warehouses
 */
export async function getWarehouses(
  params?: {
    isActive?: boolean;
    isDefault?: boolean;
  },
  options?: RequestOptions,
): Promise<WarehouseResponse> {
  try {
    const queryParams = new URLSearchParams();
    if (params?.isActive !== undefined) queryParams.append('isActive', String(params.isActive));
    if (params?.isDefault !== undefined) queryParams.append('isDefault', String(params.isDefault));

    const queryString = queryParams.toString();
    const url = `/admin/warehouses${queryString ? `?${queryString}` : ''}`;

    // Ensure Authorization header is included
    const { useAuthStore } = require('@/stores/authStore');
    const token = useAuthStore.getState().token || getAuthToken();

    const requestOptions: any = {
      method: 'GET',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      ...(options || {}),
    };

    const response = await request<WarehouseResponse>(url, requestOptions);
    return response;
  } catch (error: any) {
    // console.error('❌ Get Warehouses - Error:', {
    //   status: error?.response?.status,
    //   statusText: error?.response?.statusText,
    //   data: error?.response?.data,
    //   message: error?.message,
    // });
    throw error;
  }
}

/**
 * Get warehouse by ID
 * GET /admin/warehouses/:id/detail
 */
export async function getWarehouseById(
  id: string,
  options?: RequestOptions,
): Promise<WarehouseResponse> {
  try {
    // Ensure Authorization header is included
    const { useAuthStore } = require('@/stores/authStore');
    const token = useAuthStore.getState().token || getAuthToken();

    const requestOptions: any = {
      method: 'GET',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      ...(options || {}),
    };

    const response = await request<WarehouseResponse>(`/admin/warehouses/${id}/detail`, requestOptions);
    return response;
  } catch (error: any) {
    // console.error('❌ Get Warehouse by ID - Error:', {
    //   status: error?.response?.status,
    //   statusText: error?.response?.statusText,
    //   data: error?.response?.data,
    //   message: error?.message,
    //   id,
    // });
    throw error;
  }
}

/**
 * Update warehouse
 * PUT /admin/warehouses/:id
 */
export async function updateWarehouse(
  id: string,
  body: UpdateWarehouseParams,
  options?: RequestOptions,
): Promise<WarehouseResponse> {
  try {
    // Ensure Authorization header is included
    const { useAuthStore } = require('@/stores/authStore');
    const token = useAuthStore.getState().token || getAuthToken();

    const requestOptions: any = {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      data: body,
      ...(options || {}),
    };

    const response = await request<WarehouseResponse>(`/admin/warehouses/${id}`, requestOptions);
    return response;
  } catch (error: any) {
    // console.error('❌ Update Warehouse - Error:', {
    //   status: error?.response?.status,
    //   statusText: error?.response?.statusText,
    //   data: error?.response?.data,
    //   message: error?.message,
    //   requestBody: body,
    //   id,
    // });
    throw error;
  }
}

/**
 * Delete warehouse
 * DELETE /admin/warehouses/:id
 */
export async function deleteWarehouse(
  id: string,
  options?: RequestOptions,
): Promise<WarehouseResponse> {
  try {
    // Ensure Authorization header is included
    const { useAuthStore } = require('@/stores/authStore');
    const token = useAuthStore.getState().token || getAuthToken();

    const requestOptions: any = {
      method: 'DELETE',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      ...(options || {}),
    };

    const response = await request<WarehouseResponse>(`/admin/warehouses/${id}`, requestOptions);
    return response;
  } catch (error: any) {
    // console.error('❌ Delete Warehouse - Error:', {
    //   status: error?.response?.status,
    //   statusText: error?.response?.statusText,
    //   data: error?.response?.data,
    //   message: error?.message,
    //   id,
    // });
    throw error;
  }
}

